import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Paper,
  Typography,
  IconButton,
  Divider,
  Avatar,
  Stack,
  Dialog,
  DialogTitle,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Backdrop
} from "@mui/material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import moment from "moment";
import { getMyDate } from "../common/Helper";
import { BIYellow } from "../assets/helpers";
import defaultProjectImage from "../assets/images/profile_picture_placeholder.png";
import defaultTradeProfile from "../assets/images/trade_placeholder.jpg";
import CloseIcon from "@mui/icons-material/Close";
import { BsTranslate } from "react-icons/bs";
import CampaignIcon from "@mui/icons-material/Campaign";
import PreviewMontageModal from "../user/montages/models/PreviewMontageModal";
import { FETCH_MONTAGES } from "../constant/Apipath";
import { toast } from "react-toastify";
import { Postrequestcall } from "../request/Postrequest";
import { showErrorToast } from "../toast/toast";

export const getMTime = (date) => {
  return moment(date).format("h:mm A");
};

const getPageLinkParams = (messageText) => {
  const params = {};
  const splitUrl = messageText?.split("&");

  splitUrl?.forEach((part) => {
    const [key, value] = part.split("=");
    if (value) {
      params[key] = decodeURIComponent(value.replace(/\+/g, " "));
    }
  });

  return params;
};

const navigateToSite = (params, navigate) => {
  switch (params?.type) {
    case "site":
      navigate(`/sites/site/${params?.id}`);
      break;
    case "product":
      navigate(`/product/${params?.id}`);
      break;
    case "trade":
      navigate(`/trade/${params?.id}`);
      break;
    case "company":
      navigate(`/company/${params?.id}`);
      break;
    case "manufacture":
      navigate(`/manufacture/${params?.id}`);
      break;
    case "project":
      navigate(`/project/${params?.id}`);
      break;
    case "montage":
      break;
    default:
      console.error("navigateToSite-> Unhandled page type: ", params?.type);
      break;
  }
};

const MessageLink = ({ message, onMontageClick }) => {
  const params = getPageLinkParams(message?.messageText);
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  let imageSrc =
    params?.picture === "undefined"
      ? defaultProjectImage
      : params?.type === "trade" || !params?.picture
        ? defaultTradeProfile
        : params?.picture;

  if (imgError) {
    imageSrc = params?.type === "trade" ? defaultTradeProfile : defaultProjectImage;
  }

  const handleClick = () => {
    if (params?.type?.trim().toLowerCase() === "montage") {
      if (onMontageClick) {
        onMontageClick(message);
      } else {
        console.error("onMontageClick is not defined");
      }
    } else {
      navigateToSite(params, navigate);
    }
  };

  return (
    <Card
      sx={{
        display: "flex",
        alignItems: "center",
        p: 2,
        cursor: "pointer",
        boxShadow: 3,
        transition: "0.3s",
        "&:hover": { boxShadow: 6 }
      }}
      onClick={handleClick}>
      <img
        src={imageSrc}
        onError={() => setImgError(true)}
        alt={params?.name || "Unknown Name"}
        style={{
          width: 60,
          height: 60,
          marginRight: 16,
          objectFit: "cover"
        }}
      />
      <Box>
        <Typography
          variant="h6"
          fontWeight={600}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 200
          }}>
          {params?.name || "Unknown Name"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {params?.type || "Unknown Type"}
        </Typography>
      </Box>
    </Card>
  );
};

const AudioMessage = ({ audioUrl }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const audioRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    const audioElement = audioRef.current;

    const onLoadedData = () => {
      setDuration(audioElement.duration);
      setIsLoaded(true);
    };

    const onTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    const onEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    audioElement.addEventListener("loadeddata", onLoadedData);
    audioElement.addEventListener("timeupdate", onTimeUpdate);
    audioElement.addEventListener("ended", onEnded);

    return () => {
      audioElement.removeEventListener("loadeddata", onLoadedData);
      audioElement.removeEventListener("timeupdate", onTimeUpdate);
      audioElement.removeEventListener("ended", onEnded);
    };
  }, []);

  const toggleAudio = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const formatTime = (time) => {
    if (isNaN(time) || time === Infinity) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        bgcolor: "#1976d2", // Blue background like your image
        borderRadius: 3,
        p: 1.5,
        gap: 1,
        maxWidth: 280,
        minWidth: 200,
        color: "white"
      }}>
      <audio ref={audioRef} src={audioUrl} preload="auto" />

      {/* Play/Pause Button */}
      <IconButton
        onClick={toggleAudio}
        disabled={!isLoaded}
        size="small"
        sx={{
          width: 32,
          height: 32,
          bgcolor: "rgba(255, 255, 255, 0.2)",
          color: "white",
          "&:hover": {
            bgcolor: "rgba(255, 255, 255, 0.3)"
          },
          "&:disabled": {
            bgcolor: "rgba(255, 255, 255, 0.1)",
            color: "rgba(255, 255, 255, 0.5)"
          }
        }}>
        {isPlaying ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
      </IconButton>

      {/* Waveform/Progress Area */}
      <Stack sx={{ flexGrow: 1, mx: 1 }}>
        {/* Waveform bars */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.3,
            height: 20,
            mb: 0.5
          }}>
          {[...Array(25)].map((_, index) => {
            const isActive = (index / 25) * 100 < progress;
            return (
              <Box
                key={index}
                sx={{
                  width: 2,
                  height: Math.random() * 12 + 4,
                  bgcolor: isActive ? "white" : "rgba(255, 255, 255, 0.3)",
                  borderRadius: 1,
                  transition: "background-color 0.2s ease"
                }}
              />
            );
          })}
        </Box>
      </Stack>

      {/* Time Display */}
      <Typography
        variant="caption"
        sx={{
          color: "rgba(255, 255, 255, 0.8)",
          fontSize: "0.75rem",
          fontWeight: 400,
          minWidth: 35,
          textAlign: "right"
        }}>
        {isPlaying ? formatTime(currentTime) : formatTime(duration)}
      </Typography>
    </Box>
  );
};

const Announcement = ({ messageText }) => {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          backgroundColor: BIYellow,
          borderRadius: 2,
          p: 1.5,
          mt: 0.5,
          mb: 0.5,
          maxWidth: 500,
          boxShadow: 0,
          gap: 1,
          width: "fit-content"
        }}>
        <CampaignIcon sx={{ color: "#b48a00", fontSize: 28, mt: 0.2, flexShrink: 0 }} />
        <Typography
          variant="body2"
          sx={{
            color: "#444",
            fontWeight: 500,
            whiteSpace: "pre-line",
            lineHeight: 1.5
          }}>
          {messageText}
        </Typography>
      </Box>
    </Box>
  );
};

const DateDivider = ({ prevDate, dateSent }) => {
  if (moment(prevDate).format("YYYY-MM-DD") !== moment(dateSent).format("YYYY-MM-DD")) {
    return (
      <Divider sx={{ width: "100%", my: 2 }}>
        <Typography
          sx={{
            color: "#888",
            fontSize: "0.75rem",
            px: 2,
            py: 0.5,
            backgroundColor: "#f5f5f5",
            borderRadius: "12px",
            textAlign: "center"
          }}>
          {getMyDate(moment(dateSent))}
        </Typography>
      </Divider>
    );
  }
  return null;
};

const ChatTranslation = ({ item }) => {
  const [translatedText, setTranslatedText] = useState(null);
  const [loading, setLoading] = useState(false);

  return (
    <div style={{ marginTop: "5px", fontSize: "12px", color: "gray" }}>
      {loading ? (
        <span>Loading translation...</span>
      ) : (
        translatedText && <span>{translatedText}</span>
      )}
    </div>
  );
};

const MessageList = ({
  msg,
  isCurrentUser,
  messageDate,
  selectedClientData,
  selectedChat,
  formClient,
  onImageClick,
  onMontageClick
}) => {
  const localStr = moment.parseZone(messageDate).local().format("h:mm a");
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("md"));
  const [showOriginal, setShowOriginal] = useState(false);

  const renderMessage = (msg) => {
    switch (msg.messageTypeId) {
      case 1:
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1
            }}>
            <Typography
              variant="body2"
              sx={{
                color: "inherit", // Inherit from parent Paper
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                fontSize: "0.95rem"
              }}>
              {isCurrentUser
                ? msg?.messageText
                : !showOriginal && msg?.translatedMessage
                  ? msg?.translatedMessage
                  : msg?.messageText}
            </Typography>
            {!isCurrentUser && (
              <span
                style={{
                  marginLeft: 6,
                  color: showOriginal ? "rgba(0,0,0,0.5)" : "#1976d2",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center"
                }}
                onClick={() => setShowOriginal((prev) => !prev)}>
                <BsTranslate size={14} />
              </span>
            )}
          </Box>
        );
      case 2:
        return (
          <img
            src={msg?.messageText}
            alt="Message"
            onClick={() => onImageClick(msg?.messageText)}
            style={{
              height: isSmallScreen ? "150px" : "200px",
              width: isSmallScreen ? "150px" : "200px",
              cursor: "pointer",
              objectFit: "cover",
              borderRadius: 5
            }}
          />
        );
      case 4:
        return <AudioMessage audioUrl={msg?.messageText} />;
      case 6:
        return <Announcement messageText={msg?.messageText} />;
      case 3:
        return (
          <>
            <MessageLink message={msg} onMontageClick={onMontageClick} />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Box
      key={msg?.id}
      sx={{
        display: "flex",
        justifyContent: isCurrentUser ? "flex-end" : "flex-start",
        mb: 2, // Increased margin for better separation
        mx: 2
      }}>
      {!isCurrentUser && formClient === "ClientChat" ? (
        <Avatar
          sx={{
            width: 32,
            height: 32,
            mr: 1.5,
            mt: 0.5,
            border: "1px solid #eee"
          }}
          src={msg?.fromPictureUrl}
        />
      ) : null}

      <Box sx={{ maxWidth: "70%" }}>
        {!msg?.isAdjacentMsg && !isCurrentUser && (
          <Typography
            sx={{
              color: "#666",
              fontSize: "0.75rem",
              mb: 0.5,
              ml: 1,
              fontWeight: 500
            }}>
            {msg?.fromFirstName?.trim() ?? ""}
          </Typography>
        )}

        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            backgroundColor: isCurrentUser
              ? selectedClientData?.AccentColor || "#1976d2"
              : "#f5f5f5",
            color: isCurrentUser ? "#fff" : "#333", // Better contrast
            borderRadius: isCurrentUser ? "18px 18px 2px 18px" : "18px 18px 18px 2px", // Chat bubble shape
            boxShadow: "0 1px 2px rgba(0,0,0,0.1)"
          }}>
          <Stack
            flexDirection={"column"}
            alignItems={isCurrentUser ? "flex-end" : "flex-start"}
            display={"flex"}>
            <Stack flexDirection={"row"} alignItems={"center"} gap={1} display={"flex"}>
              {renderMessage(msg)}
              {msg.messageTypeId === 1 && !isCurrentUser && <ChatTranslation item={msg} />}
            </Stack>

            <Typography
              sx={{
                fontSize: "0.65rem",
                color: isCurrentUser ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.5)",
                mt: 0.5,
                textAlign: "right",
                width: "100%"
              }}>
              {localStr}
            </Typography>
          </Stack>
        </Paper>
      </Box>
    </Box>
  );
};

const Message = ({ messages, selectedChat, formClient, loadingMore }) => {
  const { loginData, selectedClientData } = useSelector((state) => state?.main);
  const [imageViewer, setImageViewer] = useState({ open: false, src: "" });
  const [montageViewer, setMontageViewer] = useState({ open: false, data: null });
  const [montageLoading, setMontageLoading] = useState(false);
  let prevDate = null;
  const openImageViewer = (src) => setImageViewer({ open: true, src });
  const closeImageViewer = () => setImageViewer({ open: false, src: "" });

  function getMontageId(messageText) {
    try {
      const url = new URL(messageText);
      return url.searchParams.get("montageId");
    } catch (e) {
      return null; // invalid URL
    }
  }

  const handleMontagePreview = async (data) => {
    // Support playing directly from URL if present
    console.log("ddddddddddddddddd", data);
    if (data?.url) {
      const isVideo =
        data.url.toLowerCase().includes(".mp4") || data.url.toLowerCase().includes("video");
      const mockMontage = {
        id: data.id || "preview",
        name: data.name || "Preview",
        fileList: [
          {
            fileUrl: data.url,
            fileType: isVideo ? "video" : "image",
            imageDescription: ""
          }
        ],
        musicUrl: null
      };
      setMontageViewer({ open: true, data: mockMontage });
      return;
    }

    if (data?.id === undefined) {
      console.error("handleMontagePreview: Missing id", data);
      return;
    }
    setMontageLoading(true);
    try {
      const montageId = await getMontageId(data?.messageText);
      if (!montageId) {
        toast.error("Invalid montage link.");
        return;
      }
      const obj = {
        UserId: loginData?.userInformation?.UserId,
        PageNr: 1,
        NrOfRecPerPage: 1,
        FullSearch: "",
        IncludeRecordNr: "true",
        TypeOfObjectReturned: "",
        SearchList: [{ Id: montageId || null }],
        SortList: [{ FieldName: "Id", Direction: "DESC" }]
      };
      const response = await Postrequestcall(FETCH_MONTAGES, obj, loginData?.accessToken);
      if (response?.data?.data && response?.data?.data?.length > 0) {
        setMontageViewer({ open: true, data: response.data.data[0] });
      } else {
        console.log("Montage not found");
        showErrorToast("Montage not found");
      }
    } catch (error) {
      console.log("Failed to load montage:", error);
      showErrorToast("Failed to load montage. Please try again.");
    } finally {
      setMontageLoading(false);
    }
  };

  const closeMontageViewer = () => {
    setMontageViewer({ open: false, data: null });
  };

  return (
    <>
      {loadingMore && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            py: 2
          }}>
          <CircularProgress
            size={24}
            thickness={4}
            sx={{
              color: "primary.main",
              opacity: 0.8
            }}
          />
        </Box>
      )}

      {/* Montage Loading Overlay */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 999 }}
        open={montageLoading}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {messages.map((msg) => {
        const isCurrentUser = msg.fromUserId === loginData?.userInformation?.UserId;
        const messageDate = msg?.dateSent;
        const dateDivider = <DateDivider prevDate={prevDate} dateSent={messageDate} />;
        const announcement = <Announcement messageText={msg.messageText} />;
        prevDate = messageDate;
        return (
          <React.Fragment key={msg?.id}>
            {dateDivider}

            {msg.messageTypeId === 6 ? (
              announcement
            ) : (
              <MessageList
                fromUserId={msg?.fromUserId}
                fromFirstName={msg.fromFirstName}
                msg={msg}
                isCurrentUser={isCurrentUser}
                messageDate={messageDate}
                prevDate={prevDate}
                loginData={loginData}
                selectedClientData={selectedClientData}
                selectedChat={selectedChat}
                formClient={formClient}
                onImageClick={openImageViewer}
                onMontageClick={handleMontagePreview}
              />
            )}
          </React.Fragment>
        );
      })}

      <Dialog open={imageViewer.open} onClose={closeImageViewer} maxWidth="lg">
        <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "flex-end" }}>
          <IconButton
            aria-label="close"
            onClick={closeImageViewer}
            sx={{
              color: (theme) => theme.palette.grey[500]
            }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <img
          src={imageViewer.src}
          alt="Enlarged"
          style={{ maxWidth: "100%", maxHeight: "80vh", margin: "auto", display: "block" }}
        />
      </Dialog>

      {montageViewer.open && (
        <PreviewMontageModal
          open={montageViewer.open}
          onClose={closeMontageViewer}
          selectedMontage={montageViewer.data}
        />
      )}
    </>
  );
};

export default Message;
